import React from "react";
import PropTypes from "prop-types";

import i18n from "i18n-react";
import { useInstantSearch } from "react-instantsearch";
import { useSearch } from "hooks/useSearch";

/**
 * A default 'no results found' message
 *
 * @returns {JSX.element} A default 'no results found' message
 */
const NoResults = () => {
  const { query } = useSearch();
  return (
    <div className="no_results">
      <p>{i18n.translate("general.noResultsFor", { query })}</p>
    </div>
  );
};

/**
 * @typedef {object} NoResultsWrapperProps
 * @property {JSX.Element} Fallback A fallback component to display when no results are found. Defaults to a simple 'no
 *   results found' message.
 * @property {React.ReactNode} children The <Hits/> or <InfiniteHits/> component(s) to wrap.
 */

/**
 * A wrapper component for the instantSearch <Hits/> or <InfiniteHits/> component(s) to display a 'no results found'
 * message when applicable. (see: algolia docs "Why do InstantSearch components that use Hooks render twice?" for info
 * on the `__isArtificial` flag)
 *
 * @param {NoResultsWrapperProps} props Props
 * @returns {JSX.Element} NoResultsWrapper component with children and fallback props
 */

const NoResultsWrapper = ({ children, Fallback = NoResults }) => {
  const { results } = useInstantSearch();
  const { __isArtificial: isArtificial, nbHits } = results;

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned yet.
  if (!isArtificial && nbHits === 0) {
    return (
      <React.Fragment>
        <Fallback />
        <div hidden>{children}</div>
      </React.Fragment>
    );
  }

  return children;
};

NoResultsWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  Fallback: PropTypes.element,
};

export default NoResultsWrapper;
